import { Controller } from "@hotwired/stimulus"
import { afterStreamRender } from "../helpers/stream_render"

// Employee search for the capacity board's Mitarbeiter layout (planner board and kiosk alike).
//
// Filters the rendered rows in the browser, and inside a matching row dims the allocation bars the
// term does not point at — a caller naming only a Baustelle or its address then sees which booking
// and which days are meant, not just who.
//
// Filtering runs in the browser. That is the exception to the server-side rule on
// purpose: this board always renders EVERY eligible employee as a row (no cap, no pagination),
// so the rows on the page are the whole set — and a board rebuild per keystroke costs
// 200–400 ms. Each row carries its texts pre-folded by Planning::SearchText: the person
// (`data-search-identity`) and one text per planned Baustelle / Aufgabe (`data-search-plannables`,
// keyed like each bar's `data-bar-search-key`).
//
// The term lives in the URL (?q=) and nowhere else: replaceState on every keystroke, carried
// onto the toolbar's links and GET forms so navigating keeps it, and read back on connect. The
// URL wins over the server-rendered value because a mutation's turbo-stream re-render of the
// whole board has no `q` param to pre-fill from. Never persisted — the kiosk writes nothing.
//
// A mutation replaces a single employee ROW via turbo stream; the fresh row reconnects as a
// target and gets the current term applied, so a hidden row cannot pop back in. Anything else a
// stream re-renders (the global summary's site and expand links, after every mutation) comes back
// from the server without `q`, so the term is carried onto the page again after each stream render.
export default class extends Controller {
  static targets = ["input", "row", "count", "noMatch", "clearButton"]
  static values = { boardPath: String, matchesLabel: String }

  connect() {
    const fromUrl = new URL(window.location.href).searchParams.get("q")
    if (fromUrl !== null) this.inputTarget.value = fromUrl

    this.totalLabel = this.countTarget.textContent
    this.apply()
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  onStreamRender = (event) => {
    afterStreamRender(event, () => {
      if (this.element.isConnected) this.carryOntoLinks()
    })
  }

  search() {
    this.apply()
    this.syncUrl()
  }

  clear() {
    if (this.inputTarget.value === "") return

    this.inputTarget.value = ""
    this.search()
    this.inputTarget.focus()
  }

  rowTargetConnected(row) {
    // Also fires once per initial row; those are covered by apply() in connect().
    if (!this.tokens) return

    this.applyToRow(row)
    this.scheduleCount()
  }

  rowTargetDisconnected() {
    if (this.tokens) this.scheduleCount()
  }

  // A turbo-stream row replace is a disconnect + connect pair: count once, after both.
  scheduleCount() {
    if (this.countScheduled) return

    this.countScheduled = true
    queueMicrotask(() => {
      this.countScheduled = false
      this.updateCount()
    })
  }

  apply() {
    this.tokens = fold(this.inputTarget.value).split(/\s+/).filter(Boolean)
    this.rowTargets.forEach((row) => this.applyToRow(row))
    this.clearButtonTarget.classList.toggle("hidden", this.tokens.length === 0)
    this.updateCount()
    this.carryOntoLinks()
  }

  // A term must be found in the person or in the booking itself: the row matches when every term
  // lands in the person and one single Baustelle / Aufgabe — terms spread over two bookings point
  // at neither. Inside the row the bars whose own text holds the most terms stay bright, so
  // "müller" alone dims nothing, "müller hauptstr" dims Müller's bookings elsewhere, and "nord"
  // on Nordmann still singles out Wohnpark Nord.
  //
  // Inline display, not the `hidden` class: the row is a `flex` element, and which of two
  // display utilities wins is a stylesheet-order accident.
  applyToRow(row) {
    const identity = row.dataset.searchIdentity || ""
    const plannables = JSON.parse(row.dataset.searchPlannables || "{}")
    const score = (text) => this.score(identity, text)
    const scores = Object.values(plannables).map(score)
    const match = score("") >= 0 || scores.some((value) => value >= 0)
    const best = Math.max(0, ...scores)

    row.style.display = match ? "" : "none"
    row.toggleAttribute("data-search-excluded", !match)

    // Inline opacity, not a class — the board controller toggles `opacity-70` on a dragged bar.
    for (const bar of row.querySelectorAll("[data-bar-search-key]")) {
      const dimmed = match && best > 0 && score(plannables[bar.dataset.barSearchKey] || "") !== best

      bar.style.opacity = dimmed ? "0.3" : ""
      bar.toggleAttribute("data-search-dimmed", dimmed)
    }
  }

  // How many terms the booking's own text holds, or -1 when a term is in neither it nor the person.
  score(identity, text) {
    let found = 0
    for (const token of this.tokens) {
      if (text.includes(token)) {
        found += 1
      } else if (!identity.includes(token)) {
        return -1
      }
    }
    return found
  }

  updateCount() {
    const total = this.rowTargets.length
    const shown = this.rowTargets.filter((row) => !row.hasAttribute("data-search-excluded")).length
    const searching = this.tokens.length > 0

    this.countTarget.textContent = searching
      ? this.matchesLabelValue.replace("%{shown}", shown).replace("%{total}", total)
      : this.totalLabel
    this.noMatchTarget.classList.toggle("hidden", !searching || shown > 0 || total === 0)
  }

  // Turbo keys its restoration visits on history.state, so it is passed through unchanged.
  syncUrl() {
    const url = new URL(window.location.href)
    this.setQuery(url.searchParams)
    window.history.replaceState(window.history.state, "", url)
  }

  carryOntoLinks() {
    for (const link of document.querySelectorAll("a[href]")) {
      const url = new URL(link.href, window.location.href)
      if (!this.pointsAtBoard(url)) continue

      this.setQuery(url.searchParams)
      link.href = url.pathname + url.search + url.hash
    }

    for (const form of document.querySelectorAll("form")) {
      if ((form.method || "get").toLowerCase() !== "get") continue
      if (!this.pointsAtBoard(new URL(form.action, window.location.href))) continue

      let field = form.querySelector("input[type=hidden][name=q]")
      if (!field) {
        field = document.createElement("input")
        field.type = "hidden"
        field.name = "q"
        form.append(field)
      }
      field.value = this.term()
      field.disabled = this.term() === ""
    }
  }

  pointsAtBoard(url) {
    return url.origin === window.location.origin && url.pathname === this.boardPathValue
  }

  setQuery(params) {
    if (this.term() === "") {
      params.delete("q")
    } else {
      params.set("q", this.term())
    }
  }

  term() {
    return this.inputTarget.value.trim()
  }
}

// Mirrors Planning::SearchText.fold's first folding: lower-case, diacritics stripped, ß → ss.
// The server texts also carry the "ue" spelling, so a term typed either way matches.
function fold(value) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .replace(/ß/g, "ss")
}
